<template>
    <Toast />
    <div class="grid reporting">
        <div class="col-12">
            <div class="card">
                <h5>Outdated Admin Emails</h5>
                <p>
                    Platform admins (site and channel) whose email addresses are no longer reachable.
                    These users are not receiving system notifications. Update their address in user
                    management to restore delivery.
                </p>

                <DataTable
                    :value="rows"
                    :loading="loading"
                    :scrollable="true"
                    responsiveLayout="scroll"
                    :paginator="true"
                    :rows="50"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} admins"
                    :sortField="'last_bounced_at'"
                    :sortOrder="-1"
                >
                    <template #empty>
                        <div class="p-text-center">No outdated admin emails. Everyone's reachable.</div>
                    </template>

                    <Column field="organization" header="Organization" sortable style="min-width: 14rem">
                        <template #body="slotProps">
                            {{ slotProps.data.organization || 'N/A' }}
                        </template>
                    </Column>

                    <Column field="admin_name" header="Admin Name" sortable style="min-width: 12rem">
                        <template #body="slotProps">
                            {{ slotProps.data.admin_name || '—' }}
                        </template>
                    </Column>

                    <Column field="admin_email" header="Admin Email" sortable style="min-width: 18rem">
                        <template #body="slotProps">
                            <span class="contact-email">{{ slotProps.data.admin_email }}</span>
                            <EmailDeliverabilityBadge
                                :status="slotProps.data.status"
                                :last-bounced-at="slotProps.data.last_bounced_at"
                                :last-diagnostic="slotProps.data.last_diagnostic"
                            />
                        </template>
                    </Column>

                    <Column field="role_label" header="Role" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ slotProps.data.role_label }}
                        </template>
                    </Column>

                    <Column field="domain_has_mx" header="Domain MX" sortable style="min-width: 8rem">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.domain_has_mx === true" class="mx-yes">Yes</span>
                            <span v-else-if="slotProps.data.domain_has_mx === false" class="mx-no">No</span>
                            <span v-else>—</span>
                        </template>
                    </Column>

                    <Column field="likely_cause" header="Likely Cause" sortable style="min-width: 18rem">
                        <template #body="slotProps">
                            {{ slotProps.data.likely_cause || '—' }}
                        </template>
                    </Column>

                    <Column field="last_bounced_at" header="Last Bounced" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.last_bounced_at) }}
                        </template>
                    </Column>

                    <Column header="" style="min-width: 8rem">
                        <template #body="slotProps">
                            <Button
                                label="Update"
                                icon="pi pi-user-edit"
                                class="p-button-sm p-button-outlined"
                                @click="openUserManagement(slotProps.data)"
                            />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import EmailDeliverabilityBadge from '../components/EmailDeliverabilityBadge.vue';
import OutdatedEmailsService from '../service/OutdatedEmailsService';

const service = new OutdatedEmailsService();

export default {
    name: 'OutdatedAdminEmails',
    components: {
        DataTable,
        Column,
        Button,
        Toast,
        EmailDeliverabilityBadge
    },
    data() {
        return {
            rows: [],
            loading: false
        };
    },
    async mounted() {
        await this.fetchRows();
    },
    methods: {
        async fetchRows() {
            this.loading = true;
            try {
                const response = await service.getOutdatedAdminEmails();
                this.rows = response?.data?.rows || [];
            } catch (err) {
                console.error('Failed to load outdated admin emails:', err);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not load report',
                    detail: 'Try refreshing the page. If the problem persists, contact support.',
                    life: 5000
                });
                this.rows = [];
            } finally {
                this.loading = false;
            }
        },
        formatDate(value) {
            if (!value) return '—';
            try {
                const d = new Date(value);
                if (isNaN(d.getTime())) return '—';
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            } catch (_) {
                return '—';
            }
        },
        openUserManagement(row) {
            this.$router.push({
                path: '/user-management',
                query: {
                    user_id: row.user_id,
                    highlight: row.admin_email
                }
            });
        }
    }
};
</script>

<style scoped>
.contact-email {
    font-family: monospace;
}
.mx-yes {
    color: #2e7d32;
    font-weight: 600;
}
.mx-no {
    color: #c62828;
    font-weight: 600;
}
</style>